import React, { useState, useEffect } from 'react';
import { auth, getConversations, getMessages, sendMessage } from '../firebase';
import './HostingMessages.css';

function HostingMessages() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const convs = await getConversations(user.uid);
      setConversations(convs);
      if (convs.length > 0) {
        setSelectedConversation(convs[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const msgs = await getMessages(conversationId);
      setMessages(msgs);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const currentConversation = conversations.find(c => c.id === selectedConversation);

  if (loading) return <div className="loading">Loading conversations...</div>;

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const user = auth.currentUser;
      if (!user) return;

      await sendMessage(selectedConversation, user.uid, user.displayName || user.email, messageText);
      setMessageText('');
      await fetchMessages(selectedConversation);
    } catch (error) {
      alert('Failed to send message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleSelectConversation = (id) => {
    setSelectedConversation(id);
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const formatMessageTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="hosting-messages-container">
      <div className="messages-list">
        <div className="messages-header">
          <h3>Messages</h3>
          <div className="unread-count">
            {conversations.filter(c => c.unread).length > 0 && (
              <span className="badge">{conversations.filter(c => c.unread).length}</span>
            )}
          </div>
        </div>

        <div className="conversations">
          {conversations.length === 0 ? (
            <div className="no-conversations">No conversations yet</div>
          ) : (
            conversations.map(conversation => {
              const convTime = conversation.lastMessageTime?.toDate ? conversation.lastMessageTime.toDate() : new Date(conversation.lastMessageTime);
              return (
                <div
                  key={conversation.id}
                  className={`conversation-item ${selectedConversation === conversation.id ? 'active' : ''}`}
                  onClick={() => handleSelectConversation(conversation.id)}
                >
                  <div className="conversation-avatar">{conversation.guestName?.charAt(0) || 'G'}</div>
                  <div className="conversation-preview">
                    <div className="conversation-name">{conversation.guestName}</div>
                    <div className="conversation-property">{conversation.listingTitle}</div>
                    <div className="conversation-message">{conversation.lastMessage || 'No messages yet'}</div>
                  </div>
                  <div className="conversation-time">{formatTime(convTime)}</div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="messages-chat">
        {currentConversation ? (
          <>
            <div className="chat-header">
              <div>
                <h3>{currentConversation.guestName}</h3>
                <p>{currentConversation.listingTitle}</p>
              </div>
              <button className="info-btn">ℹ️</button>
            </div>

            <div className="messages-thread">
              {messages.map(message => {
                const user = auth.currentUser;
                const fromHost = message.userId === user?.uid;
                return (
                  <div
                    key={message.id}
                    className={`message ${fromHost ? 'from-host' : 'from-guest'}`}
                  >
                    <div className="message-bubble">
                      <p>{message.text}</p>
                      <span className="message-time">
                        {message.createdAt?.toDate ? formatMessageTime(message.createdAt.toDate()) : formatMessageTime(new Date(message.createdAt))}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="message-input-area">
              <textarea
                placeholder="Type your message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={sending}
              />
              <button 
                onClick={handleSendMessage} 
                disabled={!messageText.trim() || sending}
                className="send-btn"
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </>
        ) : conversations.length === 0 ? (
          <div className="no-chat-selected">
            <p>No conversations yet. When guests book, you'll see messages here.</p>
          </div>
        ) : (
          <div className="no-chat-selected">
            <p>Select a conversation to start messaging</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default HostingMessages;
